import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { LoadingSpinner, Table, TextLink, Tooltip } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { EyeIcon, EditIcon, Trash2Icon } from 'lucide-react';
import { useSecurityGroupMutations } from '../hooks/useSecurityGroupMutations';
import type { SecurityGroupRecord } from '../types/security-admin.types';
import { invoicingModeLabel } from '../utils/invoicingMode';

interface Props {
  data: SecurityGroupRecord[];
  isLoading?: boolean;
}

export const SecurityGroupDataTable: React.FC<Props> = ({ data, isLoading = false }) => {
  const navigate = useNavigate();
  const { deleteMutation } = useSecurityGroupMutations();

  const handleDelete = (group: SecurityGroupRecord) => {
    // Match the detail-page confirm copy so the user sees the same prompt
    // regardless of which entry point triggered the delete.
    if (!window.confirm(`Delete security group "${group.name}"? This cannot be undone.`)) return;
    deleteMutation.mutate(group.id);
  };

  const columns = useMemo<ColumnDef<SecurityGroupRecord>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: (info) => (
        <span className="font-semibold text-sm">{info.getValue() as string}</span>
      ),
    },
    {
      id: 'dealers_count',
      header: 'Dealers',
      cell: (info) => (
        <span className="text-sm">{info.row.original.dealers_count ?? 0}</span>
      ),
    },
    {
      id: 'products_count',
      header: 'Products',
      cell: (info) => (
        <span className="text-sm">{info.row.original.products_count ?? 0}</span>
      ),
    },
    {
      id: 'invoicing_mode',
      header: 'Invoicing mode',
      cell: (info) => (
        <span className="text-sm">{invoicingModeLabel(info.row.original.invoicing_mode)}</span>
      ),
    },
    {
      id: 'users_count',
      header: 'Users',
      cell: (info) => (
        <span className="text-sm">{info.row.original.users_count ?? 0}</span>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: (info) => {
        const v = info.getValue() as string;
        return <span className="text-sm">{v ? new Date(v).toLocaleDateString() : '—'}</span>;
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: (info) => {
        const group = info.row.original;
        const { id, name } = group;
        // Icon-only Buttons: `Tooltip` does NOT set an accessible name
        // (visual-only hint), so each action gets an explicit `ariaLabel`
        // scoped to the row's group name so screen-reader users can tell
        // identical icons apart between rows.
        return (
          <div className="flex space-x-2">
            <Tooltip content="View group">
              <TextLink
                onClick={() => navigate(`/admin/security-groups/${id}`)}
                color="primary"
                startIcon={<EyeIcon size={20} />}
                ariaLabel={`View security group ${name}`}
              />
            </Tooltip>
            <PermissionGuard permission={{ resource: 'security_group', action: 'edit' }}>
              <Tooltip content="Edit group">
                <TextLink
                  onClick={() => navigate(`/admin/security-groups/${id}/edit`)}
                  color="secondary"
                  startIcon={<EditIcon size={18} />}
                  ariaLabel={`Edit security group ${name}`}
                />
              </Tooltip>
            </PermissionGuard>
            <PermissionGuard permission={{ resource: 'security_group', action: 'delete' }}>
              <Tooltip content="Delete group">
                <TextLink
                  onClick={() => handleDelete(group)}
                  color="error"
                  startIcon={<Trash2Icon size={18} />}
                  disabled={deleteMutation.isPending}
                  ariaLabel={`Delete security group ${name}`}
                />
              </Tooltip>
            </PermissionGuard>
          </div>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [deleteMutation.isPending]);

  if (isLoading) {
    return <LoadingSpinner size="xl" className="py-12" message="Loading security groups..." />;
  }

  return <Table<SecurityGroupRecord> data={data} columns={columns} enableSorting={false} />;
};
